import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { CASE_CLIENT_ROLE_LABELS, CASE_TYPE_LABELS, type ClientDetail } from '@lawyer/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ClientDialog } from '../components/clients/ClientDialog'
import { CompaniesPanel } from '../components/clients/CompaniesPanel'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { DocumentsPanel } from '../components/documents/DocumentsPanel'
import { TaskDialog } from '../components/tasks/TaskDialog'
import { TasksTable } from '../components/tasks/TasksTable'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  )
}

export function ClientDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.client(id),
    queryFn: () => api.get<ClientDetail>(`/clients/${id}`),
    enabled: Boolean(id),
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/clients/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      navigate('/clients')
    },
  })

  if (isPending) return <LoadingBlock />
  if (error) return <ErrorAlert error={error} onRetry={() => void refetch()} />
  if (!data) return <EmptyState title="הלקוח לא נמצא" />

  return (
    <>
      <PageHeader
        title={data.displayName}
        subtitle={[data.nationalId && `ת״ז ${data.nationalId}`, data.passport && `דרכון ${data.passport}`]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <IconButton onClick={() => navigate('/clients')} title="חזרה לרשימה">
              <ArrowForwardIcon />
            </IconButton>
            <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              עריכה
            </Button>
            <Button color="error" onClick={() => setConfirmDelete(true)}>
              מחיקה
            </Button>
          </>
        }
      />

      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      <Tabs value={tab} onChange={(_e, next) => setTab(next)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="פרטים" />
        <Tab label={`חברות (${data.companies.length})`} />
        <Tab label={`תיקים (${data.cases.length})`} />
        <Tab label="משימות" />
        <Tab label="מסמכים" />
      </Tabs>

      {tab === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Grid container spacing={2.5}>
            <DetailRow label="שם פרטי בעברית" value={data.hebrewFirstName} />
            <DetailRow label="שם משפחה בעברית" value={data.hebrewLastName} />
            <DetailRow label="שם פרטי באנגלית" value={data.firstName} />
            <DetailRow label="שם משפחה באנגלית" value={data.lastName} />
            <DetailRow label="מספר תעודת זהות" value={data.nationalId} />
            <DetailRow label="מספר דרכון" value={data.passport} />
            <DetailRow label="מדינה" value={data.country} />
            <DetailRow label="מספר ישות" value={data.entityNumber} />
            <DetailRow label="דואר אלקטרוני" value={data.email} />
            <DetailRow label="טלפון נייד" value={data.phone} />
          </Grid>
        </Paper>
      ) : null}

      {tab === 1 ? <CompaniesPanel clientId={id} companies={data.companies} /> : null}

      {tab === 2 ? (
        data.cases.length === 0 ? (
          <EmptyState title="הלקוח אינו משויך לתיקים" />
        ) : (
          <Stack spacing={1}>
            {data.cases.map((row) => (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/cases/${row.id}`)}
                    sx={{ font: 'inherit', flexGrow: 1, textAlign: 'right' }}
                  >
                    {row.name}
                  </Link>
                  <Chip size="small" label={CASE_TYPE_LABELS[row.caseType]} />
                  <Chip size="small" variant="outlined" label={CASE_CLIENT_ROLE_LABELS[row.clientRole]} />
                  {row.projectName ? <Chip size="small" variant="outlined" label={row.projectName} /> : null}
                  {row.companyName ? (
                    <Chip size="small" color="secondary" variant="outlined" label={`דרך ${row.companyName}`} />
                  ) : null}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )
      ) : null}

      {tab === 3 ? (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button startIcon={<AddIcon />} onClick={() => setTaskOpen(true)}>
              משימה חדשה ללקוח
            </Button>
          </Stack>
          <Box>
            <TasksTable
              query={{ clientId: id, pageSize: 200 }}
              onOpenTask={setOpenTaskId}
              hiddenColumns={['clientName']}
              height={520}
            />
          </Box>
        </>
      ) : null}

      {tab === 4 ? (
        <DocumentsPanel
          scope={{ clientId: id }}
          emptyText="אין מסמכים בכרטיס הלקוח"
          quickUploads={['תעודת זהות', 'דרכון']}
          ownerName={data.displayName}
        />
      ) : null}

      <ClientDialog open={editOpen} client={data} onClose={() => setEditOpen(false)} />
      <TaskDialog
        open={taskOpen}
        defaults={{ clientId: id }}
        onClose={() => setTaskOpen(false)}
      />
      <TaskDialog open={Boolean(openTaskId)} taskId={openTaskId} onClose={() => setOpenTaskId(null)} />

      <ConfirmDialog
        open={confirmDelete}
        title="מחיקת לקוח"
        message="הלקוח, החברות שלו, שיוכיו בתיקים והמסמכים שלו יימחקו לצמיתות. להמשיך?"
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}
